import axios from "axios";
import { useContext } from "react";
import useSWR from "swr";
import { verifiedAffiliateEnvironment } from "./affiliateEnvironment";
import { AuthContext } from "../../../context/AuthContext";
import {
  AffiliateFillHistory,
  AffiliateFillRecord,
} from "../../../interfaces/affiliate";
import { ADMIN_API_URL, isAffiliateEnvironment } from "../../../utils/env";

export type AffiliateHistoryScope = "affiliate" | "wallet";

const validFill = (fill: AffiliateFillRecord) =>
  fill?.request &&
  [
    fill.request.fill_id,
    fill.request.wallet,
    fill.request.affiliate_id,
    fill.request.at,
    fill.fee_paid,
    fill.fee_waived,
    fill.accrual?.cash_usd,
    fill.accrual?.dao_aevo_usd,
    fill.accrual?.earned_points,
  ].every((value) => typeof value === "string" && value.length > 0);

export const useAffiliateHistory = (
  verifiedTestnet: boolean,
  scope: AffiliateHistoryScope,
  address: string,
  generation: number,
  cursor: string
) => {
  const { tokenId, queryConfig } = useContext(AuthContext);
  const allowed =
    isAffiliateEnvironment() && verifiedTestnet && Boolean(tokenId);
  const params = new URLSearchParams({ [scope]: address, limit: "100" });
  if (cursor) params.set("cursor", cursor);
  const url = ADMIN_API_URL(`affiliates/fills?${params.toString()}`);
  return useSWR<AffiliateFillHistory>(
    allowed && /^0x[0-9a-f]{40}$/i.test(address)
      ? [url, tokenId, generation]
      : null,
    async () => {
      const { data } = await axios.get<AffiliateFillHistory>(
        url,
        queryConfig()
      );
      if (
        !verifiedAffiliateEnvironment(data) ||
        data.source !== "clickhouse" ||
        data.generation !== generation ||
        typeof data.history_after !== "string" ||
        !Array.isArray(data.fills) ||
        !data.fills.every(validFill) ||
        typeof data.next_cursor !== "string" ||
        (data.next_cursor !== "" && data.next_cursor === cursor)
      ) {
        throw new Error(
          "History did not match the current affiliate history scope. Refresh state and try again."
        );
      }
      return data;
    },
    {
      keepPreviousData: false,
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    }
  );
};
